import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArtInitial from "./views/ArtInitial";
import ArtEdit from "./views/ArtEdit";
import { useArt } from "../../backend/useArt";
import { useArt3D } from "../../backend/useArt3D";
import { useUser } from "../../backend/useUser";
import LoadingSpinner from "../../ui/components/LoadingSpinner";
import UpsellModalTrigger from "../pricing/UpsellModalTrigger";
import { useConfig } from "../../utils/config";

export type ArtType = '2D' | '3D';

interface ArtProps {

}

const Art = ({  }: ArtProps) => {
    const config = useConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const { artId: urlArtId } = useParams();
    const [artId, setArtId] = useState(urlArtId);
    const [artName, setArtName] = useState(urlArtId ? '...' : 'New Art Project');
    const [isLoading, setIsLoading] = useState(!!urlArtId);
    const [artType, setArtType] = useState<ArtType>(null);
    const { createArt, getArt } = useArt();
    const { createArt3D, getArt3D } = useArt3D();
    const { user } = useUser();
    const [hasCreatedArt, setHasCreatedArt] = useState(false);
    
    // Check for 'new' query parameter to auto-create art
    useEffect(() => {
        if (!urlArtId && user && !hasCreatedArt) {
            const searchParams = new URLSearchParams(location.search);
            const newArtType = searchParams.get('new');
            
            if (newArtType === '2d') {
                setHasCreatedArt(true);
                handleCreateClick('2D');
            } else if (newArtType === '3d') {
                setHasCreatedArt(true);
                handleCreateClick('3D');
            }
        }
    }, [location.search, user, urlArtId, hasCreatedArt]);

    // First effect to set the art type based on URL path
    useEffect(() => {
        if (urlArtId) {
            const is3D = location.pathname.startsWith('/3d/');
            setArtType(is3D ? '3D' : '2D');
            setArtId(urlArtId);
        }
    }, [urlArtId, location.pathname]);
    
    // Second effect to load art data after art type is set
    useEffect(() => {
        if (urlArtId && artType) {
            setIsLoading(true);
            
            // Load art data based on type
            const loadArt = async () => {
                try {
                    let artData;
                    if (artType === '3D') {
                        artData = await getArt3D(urlArtId);
                    } else if (artType === '2D') {
                        artData = await getArt(urlArtId);
                    }
                    
                    if (artData) {
                        setArtName(artData.art_name || 'Untitled Art');
                    }
                    setIsLoading(false);
                } catch (error) {
                    console.error('Error loading art:', error);
                    setIsLoading(false);
                }
            };
            
            loadArt();
        }
    }, [urlArtId, artType, getArt, getArt3D]);

    const handleNotLoggedIn = () => {
        if (!user) {
            // console.log('User not authenticated, storing redirect path:', location.pathname);
            localStorage.setItem('redirectPath', location.pathname);
            navigate('/signup', { state: { from: location } });
            return true;
        }
        return false;
    }

    const handleCreateClick = async (artType: ArtType) => {
        if (handleNotLoggedIn()) return;
        
        try {
            let newArtId;
            if (artType === '3D') {
                const art3D = await createArt3D('New Art Project');
                newArtId = art3D?.id;
                navigate(`/3d/${newArtId}`);
            } else {
                const art = await createArt('New Art Project');
                newArtId = art?.id;
                navigate(`/art/${newArtId}`);
            }
        } catch (error) {
            console.error('Error creating art:', error);
        }
    };

    const handleHomeClick = () => {
        navigate('/art');
    };
    
    // Render based on URL pattern
    if (!urlArtId) {
        return (
            <>
                <UpsellModalTrigger colorMode="light" />
                <ArtInitial 
                    config={config}
                    onCreateClick={handleCreateClick}
                />
            </>
        )
    } else {
        if (!artId || !artType) {
            return <LoadingSpinner />;
        } else {
            return (
                <ArtEdit 
                    onHomeClick={handleHomeClick}
                    artId={artId}
                    name={artName}
                    isLoading={isLoading}
                    artType={artType}
                />
            )
        }
    }
};

export default Art;